import React from 'react';
import { BudgetCategory } from './types.ts';
import { TrendingUp, Info } from 'lucide-react';

interface BudgetDetailsProps {
  category: BudgetCategory | null;
  totalBudget: number;
}

export const BudgetDetails: React.FC<BudgetDetailsProps> = ({ category, totalBudget }) => {
  
  if (!category) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="w-24 h-24 bg-gradient-to-br from-pink-500/20 to-orange-500/20 backdrop-blur-sm rounded-3xl flex items-center justify-center mb-6 border border-white/30">
          <span className="text-4xl">📊</span>
        </div>
        <h5 className="text-xl font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-4">
          Select a Category
        </h5>
        <p className="text-sm text-gray-600/80 max-w-sm leading-relaxed bg-white/30 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
          Click on any section of the budget chart to view detailed breakdown and cost factors
        </p>
      </div>
    );
  }

  const amount = Math.round((category.percentage / 100) * totalBudget);

  return (
    <div className="space-y-8">
      {/* Category Header */}
      <div className="pb-6 border-b border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h5 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            {category.name}
          </h5>
          <div className="flex items-center space-x-3">
            <div 
              className="w-6 h-6 rounded-full shadow-lg border-2 border-white/30" 
              style={{ backgroundColor: category.color }}
            />
          </div>
        </div>
        <div className="flex items-baseline space-x-4">
          <span className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            ${amount.toLocaleString()}
          </span>
          <div className="flex items-center space-x-2">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-orange-500 px-3 py-1 rounded-full shadow-lg">
              {category.percentage.toFixed(1)}% of total
            </span>
          </div>
        </div>
      </div>
      
      {/* Cost Factors */}
      <div>
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-lg">
            <Info className="w-5 h-5 text-blue-600" />
          </div>
          <h6 className="text-lg font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            Cost Factors
          </h6>
        </div>
        <div className="space-y-4">
          {category.factors.map((factor, index) => (
            <div 
              key={index} 
              className="group bg-white/30 backdrop-blur-sm rounded-2xl p-5 border border-white/30 hover:bg-white/50 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gradient-to-br from-pink-400/20 to-orange-400/20 rounded-xl flex-shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 border border-white/20">
                  <span className="text-2xl">{factor.icon}</span>
                </div>
                <div className="flex-1 pt-1">
                  <p className="text-sm text-gray-700 group-hover:text-gray-900 leading-relaxed font-medium transition-colors">
                    {factor.text}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};